import SearchBar from "@design-system/Inputs/SearchBar";
import { FlexCol, FlexRow } from "@design-system/Layout/Flex";
import { PageContainer } from "@design-system/Layout/PageContainer";
import { Text } from "@design-system/Typography/Text";
import {
    KbDocumentType,
    KnowledgeBaseItemStatus,
    KnowledgeBaseItemType,
} from "@generated/client/graphql";
import { useAtom } from "jotai";
import { useEffect, useState, type FC } from "react";
import { Trans } from "react-i18next";
import { Link, useParams } from "react-router-dom";
import { AddItemButton } from "./components/AddButtons";
import {
    DropdownDocumentTypeFilter,
    DropdownEntityFilter,
    DropdownStatusFilter,
} from "./components/DropdownFilter";
import { EditButtons } from "./components/EditButtons";
import { KnowledgeBaseHeader } from "./components/Header";
import { SideBar } from "./components/SideBar";
import { KnowledgeBaseTable } from "./components/Table";
import { activeTypeAtom, button_props } from "./ctx";
import { useKnowledgeBasePageTranslations } from "./i18n";

export const KnowledgeBase: FC = () => {
    const { t, keys } = useKnowledgeBasePageTranslations();
    const { org_uname } = useParams();

    const [searchString, setSearchString] = useState<string | undefined>(
        undefined,
    );
    const [selectedStatuses, setSelectedStatuses] = useState<
        KnowledgeBaseItemStatus[]
    >([]);
    const [selectedDocumentTypes, setSelectedDocumentTypes] = useState<
        KbDocumentType[]
    >([]);
    const [selectedEntityIds, setSelectedEntityIds] = useState<string[]>([]);
    const [activeType, setActiveType] = useAtom(activeTypeAtom);

    const { to } = button_props[activeType];

    useEffect(() => {
        setSelectedStatuses([]);
        setSelectedDocumentTypes([]);
        setSelectedEntityIds([]);
        setSearchString(undefined);
    }, [activeType]);

    return (
        <PageContainer p="4" gap="4">
            <KnowledgeBaseHeader p="6" gap="2" />
            <FlexRow className="h-full" gap="4">
                <SideBar
                    activeType={activeType}
                    setActiveType={setActiveType}
                />
                <FlexCol
                    w="full"
                    h="full"
                    className="bg-secondary rounded-lg border-tertiary border min-w-0"
                >
                    <KnowledgeBaseHeader
                        title={t(activeType)}
                        subtitle={
                            <Trans
                                i18nKey={`${activeType}_subtitle`}
                                t={t}
                                key={`${activeType}_subtitle`}
                                components={{
                                    1: (
                                        <Link
                                            to={to(org_uname)[0]}
                                            className="text-brand-primary text-base underline"
                                        />
                                    ),
                                    2: (
                                        <Link
                                            to={to(org_uname)[1]}
                                            className="text-brand-primary text-base underline"
                                        />
                                    ),
                                    3: (
                                        <Link
                                            to={to(org_uname)[2]}
                                            className="text-brand-primary text-base underline"
                                        />
                                    ),
                                }}
                            />
                        }
                        noBorder
                        p="4"
                        gap="1"
                    />
                    <FlexCol gap="3" px="4">
                        {activeType !== KnowledgeBaseItemType.CustomItem && (
                            <FlexRow gap="2" alignItems="center">
                                <Text variant="body-bold">
                                    {t("filter_by")}
                                </Text>
                                {[
                                    KnowledgeBaseItemType.Indicator,
                                    KnowledgeBaseItemType.Objective,
                                ].includes(activeType) && (
                                    <DropdownEntityFilter
                                        selectedEntityIds={selectedEntityIds}
                                        setSelectedEntityIds={
                                            setSelectedEntityIds
                                        }
                                    />
                                )}
                                {activeType !==
                                    KnowledgeBaseItemType.Indicator && (
                                    <DropdownStatusFilter
                                        selectedStatuses={selectedStatuses}
                                        setSelectedStatuses={
                                            setSelectedStatuses
                                        }
                                    />
                                )}
                                {activeType ===
                                    KnowledgeBaseItemType.Document && (
                                    <DropdownDocumentTypeFilter
                                        selectedTypes={selectedDocumentTypes}
                                        setSelectedTypes={
                                            setSelectedDocumentTypes
                                        }
                                    />
                                )}
                            </FlexRow>
                        )}
                        <FlexRow
                            gap="2"
                            alignItems="center"
                            className="w-full justify-between"
                        >
                            <SearchBar
                                placeholder={t(keys.search_bar_placeholder, {
                                    context: activeType,
                                })}
                                searchString={searchString}
                                setSearchString={setSearchString}
                                className="w-[250px] bg-white"
                            />
                            {activeType === KnowledgeBaseItemType.CustomItem ? (
                                <AddItemButton />
                            ) : (
                                <EditButtons activeType={activeType} />
                            )}
                        </FlexRow>
                    </FlexCol>

                    <KnowledgeBaseTable
                        searchString={searchString}
                        selectedStatuses={selectedStatuses}
                        selectedTypes={selectedDocumentTypes}
                        selectedEntityIds={selectedEntityIds}
                        activeType={activeType}
                    />
                </FlexCol>
            </FlexRow>
        </PageContainer>
    );
};
